import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO, differenceInDays, addDays, startOfMonth } from 'date-fns';
import { Target, Plus, X, Trash, TrendUp, CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const METRIC_LABELS = {
  one_rm: 'Est. 1RM',
  top_set_weight: 'Top Set',
  distance_m: 'Distance',
  duration_seconds: 'Duration',
};
const METRIC_UNITS = {
  one_rm: 'kg',
  top_set_weight: 'kg',
  distance_m: 'm',
  duration_seconds: 's',
};

/**
 * Goals dashboard component. Can filter to one exercise via `exerciseId` prop
 * (used on the Exercise Detail screen). When no prop given, shows all goals.
 */
export default function GoalsPanel({ exerciseId, exercises }) {
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [exerciseId]);

  const load = async () => {
    try {
      const url = exerciseId
        ? `${API_URL}/goals?exercise_id=${exerciseId}`
        : `${API_URL}/goals`;
      const { data } = await axios.get(url, { withCredentials: true });
      setGoals(data);
    } finally { setLoading(false); }
  };

  const save = async (payload) => {
    await axios.post(`${API_URL}/goals`, payload, { withCredentials: true });
    await load();
    setShowForm(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    await axios.delete(`${API_URL}/goals/${id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="ios-card p-4 mb-4" data-testid="goals-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={16} weight="fill" className="text-userRed" />
          <h2 className="text-lg font-black font-heading uppercase text-graphite">Goals</h2>
          {goals.length > 0 && (
            <span className="text-xs font-semibold text-graphite/50 tabular-nums">
              {goals.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="h-9 px-3 rounded-ios-pill bg-userRed text-bone text-[10px] uppercase tracking-[0.18em] font-black flex items-center gap-1"
          data-testid="add-goal-button"
        >
          <Plus size={12} weight="bold" /> New Goal
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-graphite/40">Loading…</p>
      ) : goals.length === 0 ? (
        <p className="text-sm text-graphite/60">
          Set a target — like "100kg bench press by Aug 1" — and track your progress after every session.
        </p>
      ) : (
        <div className="space-y-2">
          {goals.map((g) => <GoalRow key={g.id} goal={g} onDelete={() => del(g.id)} />)}
        </div>
      )}

      {showForm && (
        <GoalFormModal
          exercises={exercises || []}
          lockedExerciseId={exerciseId}
          onSave={save}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

function GoalRow({ goal, onDelete }) {
  const unit = METRIC_UNITS[goal.metric] || '';
  const metricLabel = METRIC_LABELS[goal.metric] || goal.metric;
  const pct = goal.pct || 0;
  const daysToTarget = differenceInDays(parseISO(goal.target_date), new Date());
  const done = goal.achieved || pct >= 100;

  return (
    <div className={`px-3 py-3 rounded-ios border ${done ? 'bg-userRed/5 border-userRed/25' : 'bg-boneWarm border-hairline'}`} data-testid={`goal-row-${goal.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50">
            {goal.exercise?.name || 'Exercise'} · {metricLabel}
          </p>
          <p className="text-2xl font-black font-heading tabular-nums text-graphite leading-tight">
            {fmtValue(goal.current_value, goal.metric)}
            <span className="text-graphite/40 text-sm font-normal ml-1">/ {fmtValue(goal.target_value, goal.metric)}{unit}</span>
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-graphite/50 mt-0.5">
            Target {format(parseISO(goal.target_date), 'MMM d, yyyy')}
            {' · '}
            {done
              ? <span className="text-userRed font-black">Hit it!</span>
              : daysToTarget < 0
                ? <span className="text-userRed font-bold">{Math.abs(daysToTarget)}d past</span>
                : <span>{daysToTarget}d to go</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {done && <CheckCircle size={20} weight="fill" className="text-userRed" />}
          <button
            onClick={onDelete}
            className="w-7 h-7 flex items-center justify-center text-graphite/30 hover:text-userRed"
            data-testid={`goal-delete-${goal.id}`}
            aria-label="Delete goal"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-graphite/8 rounded-ios-pill overflow-hidden">
        <div
          className={`h-full ${done ? 'bg-userRed' : 'bg-userRed/80'} rounded-ios-pill transition-all duration-700`}
          style={{ width: `${Math.min(100, Math.max(pct, 3))}%` }}
        />
      </div>
      {goal.predicted_date && !done && (
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/50 mt-2 flex items-center gap-1">
          <TrendUp size={11} weight="bold" className="text-partnerBlue" />
          On track — projected {format(parseISO(goal.predicted_date), 'MMM d, yyyy')}
        </p>
      )}
      {!goal.predicted_date && !done && (
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/40 mt-2">
          Log more sets to see your projection
        </p>
      )}
      {goal.note && (
        <p className="text-xs text-graphite/60 mt-2 italic">{goal.note}</p>
      )}
    </div>
  );
}

function fmtValue(v, metric) {
  if (metric === 'distance_m' && v >= 1000) return `${(v / 1000).toFixed(2)}k`;
  if (metric === 'duration_seconds') {
    const m = Math.floor(v / 60), s = Math.round(v % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
  return v.toString();
}

function GoalFormModal({ exercises, lockedExerciseId, onSave, onClose }) {
  const [exerciseId, setExerciseId] = useState(lockedExerciseId || '');
  const [metric, setMetric] = useState('one_rm');
  const [target, setTarget] = useState('');
  const [dateStr, setDateStr] = useState(format(addDays(new Date(), 90), 'yyyy-MM-dd'));
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');

  const exercise = exercises.find((e) => e.id === exerciseId);
  const cardio = exercise && (exercise.muscle_group === 'Cardio' || (exercise.equipment_type || '').startsWith('Cardio'));

  useEffect(() => {
    if (cardio && !['distance_m', 'duration_seconds'].includes(metric)) setMetric('distance_m');
    else if (!cardio && !['one_rm', 'top_set_weight'].includes(metric)) setMetric('one_rm');
  }, [cardio, metric]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return exercises.slice(0, 40);
    return exercises.filter((e) => e.name.toLowerCase().includes(s) || e.muscle_group.toLowerCase().includes(s)).slice(0, 40);
  }, [search, exercises]);

  const submit = () => {
    if (!exerciseId) { alert('Pick an exercise'); return; }
    const num = parseFloat(target);
    if (!num || num <= 0) { alert('Enter a target'); return; }
    onSave({
      exercise_id: exerciseId,
      metric,
      target_value: num,
      target_date: dateStr,
      note: note.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden max-h-[90vh] flex flex-col animate-slide-up">
        <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="text-2xl font-black font-heading uppercase text-graphite">New Goal</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-goal-modal">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {!lockedExerciseId && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
                Exercise
              </label>
              <div className="relative mb-2">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-graphite/40" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                  className="w-full h-10 pl-8 pr-3 rounded-ios-pill border border-hairline bg-boneWarm text-sm"
                  data-testid="goal-exercise-search"
                />
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filtered.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setExerciseId(e.id)}
                    className={`w-full text-left px-3 py-2 rounded-ios text-sm ${exerciseId === e.id ? 'bg-userRed text-bone' : 'bg-graphite/5 hover:bg-graphite/10'}`}
                    data-testid={`goal-pick-exercise-${e.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold">{e.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] opacity-70">{e.muscle_group}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Metric
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {(cardio ? ['distance_m', 'duration_seconds'] : ['one_rm', 'top_set_weight']).map((m) => (
                <button
                  key={m}
                  onClick={() => setMetric(m)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    metric === m ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`goal-metric-${m}`}
                >
                  {METRIC_LABELS[m]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Target ({METRIC_UNITS[metric]})
            </label>
            <input
              type="number"
              step="0.5"
              inputMode="decimal"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. 100"
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-base"
              data-testid="goal-target-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Target Date
            </label>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-base"
              data-testid="goal-date-input"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Note (optional)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What's your why?"
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-sm"
              data-testid="goal-note-input"
            />
          </div>

          <button
            onClick={submit}
            className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill"
            data-testid="save-goal-button"
          >
            Save Goal
          </button>
        </div>
      </div>
    </div>
  );
}
